import React, { useCallback, useMemo, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router";

import { ORGANIZATIONS, PROJECTS } from "../../../../platform/constants/routes";
import { ConfirmationDialog } from "../../../../shell/components/ConfirmationDialog";
import {
  ResourceIcon,
  ResourceLabel,
} from "../../../../shell/components/ResourceIcon";
import { useNotification } from "../../../../shell/contexts/notificationContext";
import {
  getContainerStyles,
  getStatusChipProps,
  sectionLabelSx,
} from "../../../../shell/styles/themeUtils";
import {
  useResourceMetrics,
  useResourceActivity,
  useResourceCredentials,
} from "../../hooks/useResourceDetail";
import { useResourceRealtime } from "../../hooks/useResourceRealtime";
import type { ResourceStatusTransition } from "../../hooks/useResourceRealtime";
import {
  useProjectResources,
  useResourceActions,
} from "../../hooks/useResources";
import type {
  ResourceActivityEntryUI,
  ResourceCredentialsUI,
  ResourceStatusUI,
} from "../../types";
import { copyToClipboard } from "../../utils/clipboard";
import { statusTransitionToast } from "../../utils/resourceStatusToasts";

import { ApplicationWiringSection } from "./components/ApplicationWiringSection";

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/**
 * Parse host and port from a resource endpoint string.
 * Endpoint format: `host:port/database` (e.g. `tenant-pooler.tenant-cnpg-system.svc.cluster.local:5432/dbname`).
 * Returns `{ host, port }` or null when the endpoint is missing/unparseable.
 */
function parseEndpoint(
  endpoint: string | null | undefined,
): { host: string; port: string } | null {
  if (!endpoint) return null;
  const hostPort = endpoint.split("/")[0];
  const lastColon = hostPort.lastIndexOf(":");
  if (lastColon <= 0) return null;
  const host = hostPort.slice(0, lastColon);
  const port = hostPort.slice(lastColon + 1);
  if (!host || !port) return null;
  return { host, port };
}

/**
 * Derive the PostgreSQL app role username from an endpoint string.
 * The provisioner names the role `{database_name}_app` where database_name
 * is the part after `/` in the endpoint. Used as a fallback when Vault
 * credentials are unavailable.
 */
function deriveUsername(endpoint: string | null): string | null {
  if (!endpoint) return null;
  const dbPart = endpoint.split("/")[1];
  if (!dbPart) return null;
  return `${dbPart}_app`;
}

/**
 * Build a full PostgreSQL connection string from credentials.
 * Uses the real username/password from Vault-backed credentials so the
 * displayed connection string can be copied and used immediately.
 */
function buildConnString(
  creds: ResourceCredentialsUI,
  endpoint: string,
): string {
  const parts = parseEndpoint(endpoint);
  const hostPort = parts ? `${parts.host}:${parts.port}` : endpoint;
  return `postgresql://${creds.username}:${creds.password}@${hostPort}/${creds.database}`;
}

/**
 * Human-readable label for a resource activity event.
 */
const ACTIVITY_EVENT_LABELS: Record<string, string> = {
  provisioning_started: "Provisioning started",
  vault_secret_written: "Vault secret written",
  gitops_committed: "GitOps manifest committed",
  database_ready: "Database ready",
  external_secret_ready: "External secret ready",
  provisioning_failed: "Provisioning failed",
  provisioning_retried: "Provisioning retried",
  resource_deprovisioned: "Resource deprovisioned",
};

function activityLabel(event: string): string {
  return ACTIVITY_EVENT_LABELS[event] ?? event;
}

function activityDotColor(event: string): string {
  if (event === "provisioning_failed" || event === "resource_deprovisioned") {
    return "error.main";
  }
  if (event === "database_ready" || event === "external_secret_ready") {
    return "success.main";
  }
  return "text.secondary";
}

// ── CopyField component ──────────────────────────────────────────────────────

interface CopyFieldProps {
  label: string;
  value: string;
  /** When true, value is masked as dots unless revealed. */
  maskable?: boolean;
  /** Monospace font for the value (default: true). */
  mono?: boolean;
}

/**
 * A label + value row with a copy-to-clipboard button.
 * For password fields, includes a show/hide toggle.
 */
function CopyField({
  label,
  value,
  maskable = false,
  mono = true,
}: CopyFieldProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const displayValue = maskable && !revealed ? "•".repeat(12) : value;

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: "block", mb: 0.25 }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="body2"
          fontFamily={mono ? "monospace" : "inherit"}
          sx={{
            wordBreak: "break-all",
            minWidth: 0,
            flex: 1,
            userSelect: "text",
          }}
        >
          {displayValue}
        </Typography>
        {maskable && (
          <IconButton
            size="small"
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? "Hide" : "Show"}
          >
            {revealed ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={handleCopy}
          aria-label={`Copy ${label}`}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Stack>
      {copied && (
        <Typography variant="caption" color="success.main">
          Copied to clipboard
        </Typography>
      )}
    </Box>
  );
}

// ── CopyableConnectionString component ───────────────────────────────────────

interface CopyableConnectionStringProps {
  label: string;
  connString: string;
}

/**
 * A highlighted connection string box with a copy button.
 */
function CopyableConnectionString({
  label,
  connString,
}: CopyableConnectionStringProps): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(connString);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1.5,
        bgcolor: alpha(useTheme().palette.background.paper, 0.8),
        border: `1px solid ${alpha(useTheme().palette.divider, 0.06)}`,
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
          <Typography
            variant="body2"
            fontFamily="monospace"
            sx={{ color: "primary.main", wordBreak: "break-all" }}
          >
            {connString}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleCopy}
          aria-label={`Copy ${label}`}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Stack>
      {copied && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
          Copied to clipboard
        </Typography>
      )}
    </Box>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export const ResourceDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { orgAlias, projectAlias, resourceAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
    resourceAlias: string;
  }>();

  const { resources, loading, error, reload } =
    useProjectResources(projectAlias);
  const { stop, restart, deprovision, retry, isActing } = useResourceActions();
  const { showNotification } = useNotification();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [internalExpanded, setInternalExpanded] = useState(false);

  const resource = useMemo(
    () => resources.find((r) => r.alias === resourceAlias) ?? null,
    [resources, resourceAlias],
  );

  const { metrics, loading: metricsLoading } =
    useResourceMetrics(resourceAlias);
  const {
    activity,
    total: activityTotal,
    loading: activityLoading,
    loadMore,
    hasMore,
  } = useResourceActivity(resourceAlias);
  const {
    credentials,
    loading: credentialsLoading,
    error: credentialsError,
    reload: reloadCredentials,
  } = useResourceCredentials(resourceAlias);

  const projectPath = projectAlias
    ? PROJECTS.DETAILS.getPath(orgAlias ?? "", projectAlias)
    : ORGANIZATIONS.ROOT.path;

  useResourceRealtime(
    projectAlias,
    reload,
    useCallback(
      (transition: ResourceStatusTransition) => {
        const toast = statusTransitionToast(transition);
        if (toast) {
          showNotification(toast.message, toast.severity);
        }
      },
      [showNotification],
    ),
  );

  const handleStop = async () => {
    if (!resource) return;
    await stop(resource.alias);
    reload();
  };

  const handleRestart = async () => {
    if (!resource) return;
    await restart(resource.alias);
    reload();
  };

  const handleRetry = async () => {
    if (!resource) return;
    await retry(resource.alias);
    reload();
  };

  const handleDelete = async () => {
    if (!resource) return;
    const ok = await deprovision(resource.alias);
    if (ok) {
      void navigate(projectPath);
    }
    setShowDeleteConfirm(false);
  };

  const status = (resource?.status ?? "provisioning") as ResourceStatusUI;
  const statusProps = getStatusChipProps(status);
  const isRunning = status === "running";
  const isStopped = status === "stopped";
  const isFailed = status === "failed";
  const spec = (resource?.spec ?? {}) as Record<string, unknown>;
  const errorMessage =
    typeof spec.errorMessage === "string" ? spec.errorMessage : null;
  // Effective endpoints: prefer resource.endpoint, fall back to
  // credentials.endpoint (the resource list API may return null when
  // provisioning didn't fully sync, but the credentials API has the value).
  const effectiveEndpoint = resource?.endpoint ?? credentials?.endpoint ?? null;
  const effectiveExternalEndpoint =
    resource?.externalEndpoint ?? credentials?.externalEndpoint ?? null;
  const endpointParts = parseEndpoint(effectiveEndpoint);
  const externalParts = parseEndpoint(effectiveExternalEndpoint);
  const derivedUsername = deriveUsername(effectiveEndpoint);
  const storagePct =
    metrics && metrics.storageLimitMB > 0
      ? Math.min(100, (metrics.storageUsedMB / metrics.storageLimitMB) * 100)
      : 0;

  // Build connection strings from real credentials when available
  const internalConnString =
    credentials && effectiveEndpoint
      ? buildConnString(credentials, effectiveEndpoint)
      : null;
  const externalConnString =
    credentials && effectiveExternalEndpoint
      ? buildConnString(credentials, effectiveExternalEndpoint)
      : null;

  if (loading) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <Skeleton
          variant="rectangular"
          height={80}
          sx={{ mb: 3, borderRadius: 1 }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Skeleton
              variant="rectangular"
              height={260}
              sx={{ borderRadius: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Skeleton
              variant="rectangular"
              height={220}
              sx={{ borderRadius: 1 }}
            />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !resource) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ?? "Resource not found"}
        </Alert>
        <Button
          component={RouterLink}
          to={projectPath}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Back to project
        </Button>
      </Container>
    );
  }

  return (
    <Container sx={getContainerStyles(theme)}>
      <Button
        component={RouterLink}
        to={projectPath}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: "none" }}
      >
        Back to project
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ color: "text.secondary" }}>
            <ResourceIcon type={resource.resourceType} size={28} />
          </Box>
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="h5" fontWeight={700}>
                {resource.name}
              </Typography>
              <Chip
                label={statusProps.label}
                color={statusProps.color}
                variant={statusProps.variant}
                size="small"
                sx={{ fontWeight: 500 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {ResourceLabel({ type: resource.resourceType })} ·{" "}
              {resource.provisionMode === "shared" ? "Shared" : "Dedicated"} ·{" "}
              {String(spec.region ?? "eu-central-1")}
            </Typography>
          </Box>
        </Stack>
        <Button
          color="error"
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={() => setShowDeleteConfirm(true)}
        >
          Delete resource
        </Button>
      </Stack>

      {isFailed && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{ mb: 3 }}
          action={
            <Button
              color="error"
              size="small"
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              disabled={isActing}
            >
              Retry provisioning
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Provisioning failed
          </Typography>
          {errorMessage && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {errorMessage}
            </Typography>
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          {/* ── NATS Resource (connection URL + .creds file) ── */}
          {resource.resourceType === "nats" && (
            <>
              {/* External NATS Access */}
              {(credentials?.externalUrl ?? effectiveExternalEndpoint) && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    mb: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.6),
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ ...sectionLabelSx, mb: 1 }}
                  >
                    External Access
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Connect from outside the cluster using the external NATS
                    endpoint.
                  </Typography>

                  {credentialsError && !credentialsLoading && (
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{ mb: 2 }}
                      action={
                        <Button
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={reloadCredentials}
                        >
                          Retry
                        </Button>
                      }
                    >
                      Credentials unavailable — Vault unreachable. Click retry
                      to try again.
                    </Alert>
                  )}

                  {credentialsLoading ? (
                    <Stack spacing={2}>
                      <Skeleton variant="text" height={40} />
                      <Skeleton variant="text" height={40} />
                    </Stack>
                  ) : (
                    <>
                      <CopyableConnectionString
                        label="NATS URL"
                        connString={
                          credentials?.externalUrl ??
                          effectiveExternalEndpoint ??
                          ""
                        }
                      />
                      {credentials?.creds && (
                        <Box sx={{ mt: 2 }}>
                          <CopyField
                            label="CREDENTIALS FILE (.creds)"
                            value={credentials.creds}
                            maskable
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Paper>
              )}

              {/* Internal NATS Access */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  mb: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ ...sectionLabelSx, mb: 1 }}
                >
                  Internal Access
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Connect from inside the cluster (in-cluster DNS).
                </Typography>

                {credentialsLoading ? (
                  <Stack spacing={2}>
                    <Skeleton variant="text" height={40} />
                    <Skeleton variant="text" height={40} />
                  </Stack>
                ) : (
                  <>
                    <CopyableConnectionString
                      label="NATS URL"
                      connString={credentials?.url ?? effectiveEndpoint ?? ""}
                    />
                    {credentials?.creds && (
                      <Box sx={{ mt: 2 }}>
                        <CopyField
                          label="CREDENTIALS FILE (.creds)"
                          value={credentials.creds}
                          maskable
                        />
                      </Box>
                    )}
                    {!credentials &&
                      !credentialsError &&
                      !credentialsLoading && (
                        <Alert severity="info" variant="outlined">
                          {status === "provisioning"
                            ? "Connection details will appear once provisioning is complete."
                            : "Credentials unavailable. The nats-account-controller may still be reconciling this resource."}
                        </Alert>
                      )}
                  </>
                )}
              </Paper>
            </>
          )}

          {/* ── External Access (PostgreSQL — primary connection) ── */}
          {resource.resourceType !== "nats" && externalParts && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 3,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }}
            >
              <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1 }}>
                External Access
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect from outside the cluster using the external endpoint.
              </Typography>

              {/* Credentials error state — shown when Vault is unreachable */}
              {credentialsError && !credentialsLoading && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={reloadCredentials}
                    >
                      Retry
                    </Button>
                  }
                >
                  Password unavailable — credentials service unreachable. Click
                  retry to try again.
                </Alert>
              )}

              {/* External connection string (only when credentials available) */}
              {externalConnString && (
                <CopyableConnectionString
                  label="CONNECTION STRING"
                  connString={externalConnString}
                />
              )}

              {/* Credential fields */}
              {credentialsLoading ? (
                <Stack spacing={2}>
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" height={40} />
                </Stack>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <CopyField label="HOST" value={externalParts.host} />
                  </Grid>
                  <Grid item xs={6}>
                    <CopyField label="PORT" value={externalParts.port} />
                  </Grid>
                  <Grid item xs={6}>
                    <CopyField
                      label="DATABASE"
                      value={
                        credentials?.database ??
                        effectiveEndpoint?.split("/")[1] ??
                        resource.alias
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CopyField
                      label="USERNAME"
                      value={credentials?.username ?? derivedUsername ?? "—"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {credentials ? (
                      <CopyField
                        label="PASSWORD"
                        value={credentials.password}
                        maskable
                      />
                    ) : (
                      !credentialsLoading &&
                      !credentialsError && (
                        <Alert severity="info" variant="outlined">
                          {status === "provisioning"
                            ? "Connection details will appear once provisioning is complete."
                            : "Password unavailable — credentials could not be loaded. Click retry above."}
                        </Alert>
                      )
                    )}
                  </Grid>
                </Grid>
              )}
            </Paper>
          )}

          {/* ── Internal Access (PostgreSQL — collapsed for in-cluster debugging) ── */}
          {resource.resourceType !== "nats" && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 3,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ cursor: "pointer" }}
                onClick={() => setInternalExpanded((e) => !e)}
              >
                <IconButton
                  size="small"
                  sx={{
                    p: 0.25,
                    transform: internalExpanded
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    transition: theme.transitions.create("transform"),
                  }}
                  aria-label={internalExpanded ? "Collapse" : "Expand"}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle2" sx={sectionLabelSx}>
                  Internal Access
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  (for in-cluster debugging)
                </Typography>
              </Stack>
              <Collapse in={internalExpanded}>
                <Box sx={{ mt: 2 }}>
                  {/* Internal connection string (from real credentials) */}
                  {internalConnString && (
                    <CopyableConnectionString
                      label="INTERNAL CONNECTION STRING"
                      connString={internalConnString}
                    />
                  )}

                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <CopyField
                        label="HOST"
                        value={endpointParts?.host ?? "—"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CopyField
                        label="PORT"
                        value={endpointParts?.port ?? "—"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CopyField
                        label="DATABASE"
                        value={
                          credentials?.database ??
                          effectiveEndpoint?.split("/")[1] ??
                          resource.alias
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CopyField
                        label="USERNAME"
                        value={credentials?.username ?? derivedUsername ?? "—"}
                      />
                    </Grid>
                    {credentials && (
                      <Grid item xs={12}>
                        <CopyField
                          label="PASSWORD"
                          value={credentials.password}
                          maskable
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Collapse>
            </Paper>
          )}

          {/* ── Configuration ── */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 2 }}>
              Configuration
            </Typography>
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Mode
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {resource.provisionMode === "shared" ? "Shared" : "Dedicated"}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Region
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {String(spec.region ?? "eu-central-1")}
                </Typography>
              </Stack>
              {typeof spec.backupRetentionDays === "number" && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    py: 1,
                    px: 1.5,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.paper, 0.4),
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Backup
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {`Daily automated backups retained for ${spec.backupRetentionDays} days`}
                  </Typography>
                </Stack>
              )}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDate(resource.createdAt)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* ── Danger Zone ── */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              borderColor: "error.main",
              bgcolor: alpha(theme.palette.error.main, 0.04),
            }}
          >
            <Typography
              variant="subtitle2"
              color="error.main"
              fontWeight={700}
              sx={{ mb: 1 }}
            >
              Danger Zone
            </Typography>
            <Typography variant="body2" color="error.light" sx={{ mb: 2 }}>
              Deleting this resource removes the database, role, and Vault
              credentials. This cannot be undone.
            </Typography>
            <Button
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isActing}
            >
              Delete {ResourceLabel({ type: resource.resourceType })}
            </Button>
          </Paper>
        </Grid>

        {/* ── Right column: Metrics + Activity ── */}
        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 2 }}>
              Metrics
            </Typography>

            {metricsLoading && !metrics ? (
              <Stack spacing={2}>
                <Skeleton variant="text" height={56} />
                <Skeleton
                  variant="rectangular"
                  height={6}
                  sx={{ borderRadius: 3 }}
                />
                <Skeleton variant="text" height={20} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
              </Stack>
            ) : metrics ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    STORAGE USED
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {metrics.storageUsedMB} MB
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={storagePct}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.divider, 0.1),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                        bgcolor: "primary.main",
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    of {metrics.storageLimitMB} MB logical limit
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    ACTIVE CONNECTIONS
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {metrics.activeConnections}
                  </Typography>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    QUERIES / SECOND
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {metrics.queriesPerSecond}
                  </Typography>
                </Box>
              </>
            ) : (
              <Alert severity="info" variant="outlined">
                Metrics are not available for this resource.
              </Alert>
            )}
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 2 }}>
              Recent Activity
            </Typography>

            {activityLoading && activity.length === 0 ? (
              <Stack spacing={2}>
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={40} />
              </Stack>
            ) : activity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No activity yet
              </Typography>
            ) : (
              <>
                <Stack spacing={2}>
                  {activity.map((entry: ResourceActivityEntryUI) => (
                    <Stack
                      key={entry.id}
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: activityDotColor(entry.event),
                          mt: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {activityLabel(entry.event)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(entry.createdAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
                {hasMore && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={loadMore}
                    disabled={activityLoading}
                    sx={{ mt: 1, textTransform: "none" }}
                  >
                    {activityLoading
                      ? "Loading…"
                      : `Load more (${activity.length} of ${activityTotal})`}
                  </Button>
                )}
              </>
            )}
          </Paper>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 2,
              justifyContent: "flex-start",
            }}
          >
            {isRunning && (
              <Button
                variant="outlined"
                onClick={handleStop}
                disabled={isActing}
              >
                Stop resource
              </Button>
            )}
            {isStopped && (
              <Button
                variant="outlined"
                onClick={handleRestart}
                disabled={isActing}
              >
                Restart resource
              </Button>
            )}
          </Box>

          {/* Application Wiring Section */}
          {resource?.alias && (
            <ApplicationWiringSection resourceAlias={resource.alias} />
          )}
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={showDeleteConfirm}
        title={`Deprovision ${resource.name}?`}
        description="This will permanently deprovision the resource and delete all associated data. This action cannot be undone."
        confirmLabel="Deprovision"
        destructive
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </Container>
  );
};

export default ResourceDetailPage;
